import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CandidateWebPortfoliosModal from '$components/CandidateWebPortfoliosModal/CandidateWebPortfoliosModal';
import { useAppSelector } from '$hooks/use-app-selector.hook';
import DefaultIcon2 from '$components/DefaultIcon2/DefaultIcon2';
import { Candidate } from '$types/candidates.d';
import ImageCropperDialog from '$components/ImageCropperDialog/ImageCropperDialog';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import { useUploadCandidateProfilePictureAsInternalMutation } from '$store/modules/uploads';
import { useAppDispatch } from '$hooks/use-app-dispatch.hook';

import { InitialFormStateTypes } from '../../../types/user_info';
import CandidateInfo from './CandidateInfo/CandidateInfo';
import classes from './CandidateInformationCardEmployer.module.css';
import CandidateScore from './CandidateScore/CandidateScore';
import CandidateSocialMedia from './CandidateSocialMedia/CandidateSocialMedia';
import { ReactComponent as GoldMedalIcon } from '../../../icons/GoldMedal.svg';
import {
  candidatesApi,
  useGetScoreQuery,
} from '../../../store/modules/candidates';
import ActiveValues from '../../../constants/ActiveValues';

interface CandidateInformationCardEmployerProps {
  candidate: Candidate | undefined;
  secondary?: boolean;
  candidateWorkExperience: InitialFormStateTypes['workHistory'];
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const CandidateInformationCardEmployer: FC<
  CandidateInformationCardEmployerProps
> = ({ candidate, secondary, candidateWorkExperience, setShowModal }) => {
  const [t] = useTranslation('global');
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [newProfilePictureImage, setNewProfilePictureImage] = useState<{
    image: string;
    show: boolean;
  } | null>(null);
  const [previewCropImage, setPreviewCropImage] = useState<string | null>(null);
  const profilePictureRef = useRef<HTMLInputElement | null>(null);
  const dispatch = useAppDispatch();

  const { serverUser } = useAppSelector((state) => state.auth);

  const sortedWorkHistory = candidateWorkExperience
    ?.slice()
    .sort(
      (a, b) =>
        new Date(a?.Admission.val!).getFullYear() -
        new Date(b?.Admission.val!).getFullYear()
    );

  const showScore = window.location.href.includes('employer');

  const lastJob = sortedWorkHistory?.slice(-1)[0];

  const lastJobTitle =
    lastJob?.JobTitle?.val ??
    lastJob?.JobPosition?.title ??
    lastJob?.Company?.val;

  const { data: candidateScore } = useGetScoreQuery(
    {
      id: candidate?.Id ?? 0,
    },
    { refetchOnMountOrArgChange: 3, pollingInterval: 3 * 60 * 1000 }
  );
  const [
    uploadCandidateProfilePictureAsInternal,
    { isLoading: isUpdatingProfilePicture },
  ] = useUploadCandidateProfilePictureAsInternalMutation();

  const cps = candidate?.CandidatesPositionStatus?.filter(
    (position) => position.Status === 'hired'
  );

  const handleClickChangeProfilePicture = () => {
    if (profilePictureRef.current) profilePictureRef.current.click();
  };

  const handleNewProfilePictureOnChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setNewProfilePictureImage({
          image: reader.result as string,
          show: true,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOnCompleteChangePicture = (image: Blob) => {
    setPreviewCropImage(URL.createObjectURL(image));
    setNewProfilePictureImage((prev) => {
      if (prev) return { ...prev, show: false };
      return prev;
    });
  };

  const handleCloseCropProfilePreview = () => {
    setNewProfilePictureImage(null);
    URL.revokeObjectURL(previewCropImage as string);
    setPreviewCropImage(null);
  };

  const handlePreviewPictureGoBack = () => {
    URL.revokeObjectURL(previewCropImage as string);
    setPreviewCropImage(null);
    setNewProfilePictureImage((prev) => {
      if (prev) return { ...prev, show: true };
      return prev;
    });
  };

  const handlePreviewImageSave = async () => {
    const image = await fetch(previewCropImage as string).then((resp) =>
      resp.blob()
    );
    const imageFile = new File(
      [image],
      `${candidate?.User?.FirstName}_${candidate?.User?.LastName}_${candidate?.Id}`,
      { type: 'image/png' }
    );
    const formData = new FormData();
    formData.append('file', imageFile);
    uploadCandidateProfilePictureAsInternal({
      file: formData,
      userId: candidate?.User?.Id as number,
    }).then((resp) => {
      if ('data' in resp) {
        dispatch(
          candidatesApi.util.invalidateTags([
            {
              type: 'Candidate',
              id: candidate?.Id as number,
            },
          ])
        );
        setNewProfilePictureImage(null);
        URL.revokeObjectURL(previewCropImage as string);
        setPreviewCropImage(null);
      }
    });
  };

  useEffect(() => {
    return () => {
      // Clean object url to avoid memory leak
      if (previewCropImage) URL.revokeObjectURL(previewCropImage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ModalInTailwind
        closeModal={() => setPreviewCropImage(null)}
        isOpen={!!previewCropImage}
      >
        <div className="p-8 bg-white rounded relative flex flex-col">
          <button
            type="button"
            className="text-sm absolute top-2 right-2"
            onClick={handleCloseCropProfilePreview}
            aria-label="Close"
          >
            <i className="pi pi-times text-base" />
          </button>
          <div className="text-xl font-semibold mb-4">
            {t('infoContainer.previewProfilePicture')}
          </div>
          <div className="mb-4 flex justify-center items-center max-w-64 self-center">
            <div className="w-full">
              <DefaultIcon2
                picture={previewCropImage as string}
                firstName={candidate?.User?.FirstName}
                lastName={candidate?.User?.LastName}
                addPadding
                fontSize="text-6xl"
              />
            </div>
          </div>
          <fieldset disabled={isUpdatingProfilePicture}>
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={handlePreviewPictureGoBack}
                className="text-internal-link font-semibold disabled:opacity-75 disabled:cursor-not-allowed"
              >
                {t('infoContainer.goBack')}
              </button>
              <button
                type="button"
                onClick={handlePreviewImageSave}
                className="bg-internal-main font-semibold rounded text-white px-4 py-2 disabled:opacity-75 disabled:cursor-not-allowed"
              >
                {t('common.save')}
              </button>
            </div>
          </fieldset>
        </div>
      </ModalInTailwind>
      <div className={`${classes.CandidateInfoContainer} relative`}>
        {secondary && candidate?.DoNotHire && (
          <span className="font-bold text-3xl text-red-500 animate-pulse">
            DO NOT HIRE
          </span>
        )}
        <h2 className={classes.pageTittle}>Candidate Profile</h2>
        {candidate?.CandidatesPositionStatus?.find(
          (item) => item.Status === ActiveValues.requestInterview
        ) && (
          <h3 className={classes.interviewScheduled}>
            {t('EmployerPositions.interviewScheduled')}
          </h3>
        )}
        {cps &&
          cps?.length > 0 &&
          cps.map(({ Status, Position, Id }) => {
            return (
              <div className={classes.positionStatus} key={Id}>
                <GoldMedalIcon className={classes.icon} />
                <p>
                  <span>{t('CandidateStatus.Status')}: </span>
                  {Status}
                </p>
                <p>
                  <span>{t('CandidateStatus.Company')}: </span>
                  {Position?.Company?.Name}
                </p>
              </div>
            );
          })}
        <div className={classes.Header}>
          <div className="w-52 md:w-full">
            <div className="relative">
              <DefaultIcon2
                picture={candidate?.User?.ProfilePicture?.Url}
                firstName={candidate?.User?.FirstName}
                lastName={candidate?.User?.LastName}
                addPadding
                fontSize="text-6xl"
              />
              {secondary && (
                <>
                  <input
                    type="file"
                    hidden
                    ref={profilePictureRef}
                    onChange={handleNewProfilePictureOnChange}
                    accept="image/*"
                  />
                  <button
                    type="button"
                    className="absolute bottom-0 right-2 flex group-hover:flex rounded-full bg-white shadow text-internal-main"
                    aria-label="Edit Photo"
                    title="Modify Profile Picture"
                    onClick={handleClickChangeProfilePicture}
                  >
                    <i className="pi pi-camera text-xl px-2 py-1" />
                  </button>
                  {newProfilePictureImage && newProfilePictureImage.show && (
                    <ImageCropperDialog
                      disableDismissableMask
                      onComplete={handleOnCompleteChangePicture}
                      onClose={() => {
                        setNewProfilePictureImage((prev) => {
                          if (prev) return { ...prev, show: false };
                          return prev;
                        });
                      }}
                      image={newProfilePictureImage.image}
                    />
                  )}
                </>
              )}
            </div>
          </div>
          {secondary && !showScore && (
            <CandidateScore score={candidateScore?.Score} />
          )}
          <CandidateInfo
            name={`${candidate?.User?.FirstName.split(' ')[0]}
        ${candidate?.User?.LastName.split(' ')[0]}`}
            degree={lastJobTitle ?? 'N/A'}
            isInternal={secondary}
            candidateId={candidate?.Id}
          />
          {secondary && (
            <>
              <button
                type="button"
                className={classes.contactButton}
                onClick={() => setShowModal(true)}
              >
                {t('infoContainer.basicInformation')}
              </button>
              <button
                type="button"
                className={`${classes.contactButton} mb-4`}
                onClick={() => setShowPortfolioModal(true)}
              >
                {t('infoContainer.PortfolioAndWebsites')}
              </button>
            </>
          )}
          <CandidateSocialMedia
            custom={candidate?.CustomUrl ?? ''}
            github={candidate?.GithubUrl ?? ''}
            behance={candidate?.BehanceUrl ?? ''}
            linkedIn={candidate?.LinkedinUrl ?? ''}
            whatsappNumber={candidate?.WhatsAppNumber ?? ''}
            secondary={secondary || false}
          />
        </div>
      </div>
      <CandidateWebPortfoliosModal
        isOpen={showPortfolioModal}
        onClose={() => {
          setShowPortfolioModal(false);
        }}
        candidateId={candidate?.Id as number}
        userId={serverUser?.Id as number}
        isInternal
      />
    </>
  );
};

CandidateInformationCardEmployer.defaultProps = {
  secondary: false,
};
export default CandidateInformationCardEmployer;
